import React, { Suspense, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from '@sentry/react';
import MainLayout from '@layouts/MainLayout';
import ProtectedRoute from '@components/ProtectedRoute';
import ErrorFallback from '@components/ErrorFallback';
import DevRibbon from '@components/DevRibbon';
import ToastContainer from '@components/ToastContainer/ToastContainer';
import { useAuthStore } from '@store/auth.store';
import { ROLE_ADMIN, ROLE_USER, AUTHORITIES_ADMIN, AUTHORITIES_ALL } from '@config/constants';

// Loading spinner component
const LoadingSpinner = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
    </div>
);

// Lazy load page components
const Home = React.lazy(() => import('@pages/Home'));
const Login = React.lazy(() => import('@pages/Login'));
const Activate = React.lazy(() => import('@pages/Activate'));
const RequestPasswordReset = React.lazy(() => import('@pages/RequestPasswordReset'));
const ResetPassword = React.lazy(() => import('@pages/ResetPassword'));

// Account pages
const Password = React.lazy(() => import('@pages/Password'));
const Settings = React.lazy(() => import('@pages/Settings'));
const Sessions = React.lazy(() => import('@pages/Sessions'));
const Impersonate = React.lazy(() => import('@pages/Impersonate'));

// Report pages
const StaticReports = React.lazy(() => import('@pages/StaticReports'));
const AccountLevel = React.lazy(() => import('@pages/AccountLevel'));
const GroupLevel = React.lazy(() => import('@pages/GroupLevel'));

// Admin pages
const UserAdmin = React.lazy(() => import('@pages/UserAdmin'));
const FileUpload = React.lazy(() => import('@pages/FileUpload'));
const AccountEnrollment = React.lazy(() => import('@pages/AccountEnrollment'));

// Error pages
const NotFound = React.lazy(() => import('@pages/NotFound'));
const Forbidden = React.lazy(() => import('@pages/Forbidden'));

const App = () => {
    const { fetchIdentity } = useAuthStore();

    // Attempt to restore session on app load
    useEffect(() => {
        fetchIdentity();
    }, [fetchIdentity]);

    return (
        <ErrorBoundary fallback={<ErrorFallback />}>
            <DevRibbon />
            <ToastContainer />
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    {/* Routes with MainLayout (navbar) */}
                    <Route path="/" element={<MainLayout />}>
                        {/* Public routes (no auth required) */}
                        <Route path="login" element={<Login />} />
                        <Route path="activate" element={<Activate />} />
                        <Route path="reset/request" element={<RequestPasswordReset />} />
                        <Route path="reset/finish" element={<ResetPassword />} />
                        <Route path="403" element={<Forbidden />} />
                        {/* Home - requires authentication, redirects to login if not authenticated */}
                        <Route
                            index
                            element={
                                <ProtectedRoute requiredAuthorities={AUTHORITIES_ALL}>
                                    <Home />
                                </ProtectedRoute>
                            }
                        />

                        {/* Account pages (authenticated users) */}
                        <Route
                            path="password"
                            element={
                                <ProtectedRoute requiredAuthorities={AUTHORITIES_ALL}>
                                    <Password />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="settings"
                            element={
                                <ProtectedRoute requiredAuthorities={AUTHORITIES_ALL}>
                                    <Settings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="sessions"
                            element={
                                <ProtectedRoute requiredAuthorities={[ROLE_USER]}>
                                    <Sessions />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="impersonate"
                            element={
                                <ProtectedRoute requiredAuthorities={AUTHORITIES_ADMIN}>
                                    <Impersonate />
                                </ProtectedRoute>
                            }
                        />

                        {/* Report pages */}
                        <Route
                            path="static-reports"
                            element={
                                <ProtectedRoute requiredAuthorities={AUTHORITIES_ALL}>
                                    <StaticReports />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="account-level"
                            element={
                                <ProtectedRoute requiredAuthorities={AUTHORITIES_ALL}>
                                    <AccountLevel />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="account-level/:accountId/:dateType/:fromMonth/:toMonth"
                            element={
                                <ProtectedRoute requiredAuthorities={AUTHORITIES_ALL}>
                                    <AccountLevel />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="group-level"
                            element={
                                <ProtectedRoute requiredAuthorities={AUTHORITIES_ALL}>
                                    <GroupLevel />
                                </ProtectedRoute>
                            }
                        />

                        {/* User Admin (AUTHORITIES_ADMIN) */}
                        <Route
                            path="user-admin"
                            element={
                                <ProtectedRoute requiredAuthorities={AUTHORITIES_ADMIN}>
                                    <UserAdmin />
                                </ProtectedRoute>
                            }
                        />

                        {/* File Upload (ROLE_ADMIN only) */}
                        <Route
                            path="file-upload"
                            element={
                                <ProtectedRoute requiredAuthorities={[ROLE_ADMIN]}>
                                    <FileUpload />
                                </ProtectedRoute>
                            }
                        />

                        {/* Account Enrollment (conditional on subscriber) */}
                        <Route
                            path="account-enrollment"
                            element={
                                <ProtectedRoute requiredAuthorities={AUTHORITIES_ALL}>
                                    <AccountEnrollment />
                                </ProtectedRoute>
                            }
                        />

                        {/* 404 catch-all */}
                        <Route path="*" element={<NotFound />} />
                    </Route>
                </Routes>
            </Suspense>
        </ErrorBoundary>
    );
};

export default App;
